import { Button, InfluencerTransaction } from '@hub/shared'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAmountReceivableContext } from '../../hooks/use-amount-receivable-context/use-amount-receivable-context.hook'

import './main-content.component.scoped.scss'
import './main-content.component.scss'

interface Props extends PropsWithChildren<React.HTMLAttributes<HTMLDivElement>> {
  transactionObject?: InfluencerTransaction | null
  transactionId?: string
  openModalNfeConnection?: () => any
  openModalSendNf?: () => any
  startAnticipationFlow?: (transactionId: string | undefined) => any
}

const MainContent = ({ children }: Props) => {
  return <article className="main-content">{children}</article>
}

const PaymentForecastLabel = ({ children, transactionObject, transactionId, startAnticipationFlow }: Props) => {
  const { t } = useTranslation('amountReceivable')

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!transactionId && !!loading) {
      setLoading(false)
    }
  }, [transactionId, loading])

  const infosButton: any = {
    anticipated: {
      style: {
        borderColor: 'var(--border_color)',
        fontWeight: 'bold',
        backgroundColor: 'transparent',
        color: 'var(--text_color)',
      },
      label: t('anticipated'),
      icon: 'fa-solid fa-sack-dollar',
      iconColor: 'var(--lilac)',
    },
    canAnticipate: {
      style: {
        fontWeight: 'normal',
      },
      className: 'button-discovery',
      label: t('anticipate'),
      icon: 'fa-regular fa-sack-dollar',
      iconColor: 'var(--background)',
      action: () => {
        setLoading(true)
        if (startAnticipationFlow && typeof startAnticipationFlow === 'function') {
          startAnticipationFlow(transactionObject?.transaction?.transactionId)
        }
      },
    },
  }

  const statusAnticipated = useMemo(
    () =>
      transactionObject?.transaction?.anticipationContractAccepted
        ? 'anticipated'
        : transactionObject?.anticipation?.authorization
        ? 'canAnticipate'
        : '',
    [transactionObject]
  )

  const buttonAnticipation = (status: string) =>
    !status ? null : (
      <Button
        className={`button-anticipation ${infosButton[status]?.className}`}
        onClick={infosButton[status]?.action}
        borderColor={infosButton[status]?.style?.borderColor}
        color={infosButton[status]?.style?.backgroundColor}
        textColor={infosButton[status]?.style?.color}
        loading={loading}
        style={{ flexShrink: 0 }}
      >
        <i className={`${infosButton[status]?.icon} fa-sm`} style={{ color: `${infosButton[status]?.iconColor}` }}></i>{' '}
        <span className={status === 'anticipated' ? 'text-bold' : 'text-regular'}>{infosButton[status]?.label}</span>
      </Button>
    )

  return (
    <span className="payment-forecast my-3">
      {children}
      {buttonAnticipation(statusAnticipated)}
    </span>
  )
}

const SendTaxDocumentsButton = ({ openModalNfeConnection, openModalSendNf }: Props) => {
  const { transactionObject } = useAmountReceivableContext()
  const { t } = useTranslation('amountReceivable')
  const infosButtonSelected: any = useMemo(
    () =>
      !!transactionObject?.nfs?.length
        ? 'alreadySend'
        : transactionObject?.transaction?.transactionStatus &&
          ['pending', 'review'].includes(transactionObject?.transaction?.transactionStatus)
        ? 'toSend'
        : '',
    [transactionObject]
  )
  const infosButton: any = useMemo(
    () => ({
      alreadySend: {
        label: transactionObject?.transaction?.transactionStatus === 'review' ? t('reviewDocuments') : t('seeDocuments'),
        icon: 'fa-regular fa-eye',
        action: () => {
          if (openModalSendNf && typeof openModalSendNf === 'function') {
            openModalSendNf()
          }
        },
      },
      toSend: {
        label: t('sendDocuments'),
        icon: 'fa-regular fa-circle-plus',
        action: () => {
          const openModalFunction = !transactionObject?.nfsImported?.length ? openModalSendNf : openModalNfeConnection
          if (openModalFunction && typeof openModalFunction === 'function') {
            openModalFunction()
          }
        },
      },
    }),
    [transactionObject, t, openModalSendNf, openModalNfeConnection]
  )

  if (!infosButtonSelected) {
    return null
  }

  return (
    <Button onClick={() => infosButton[infosButtonSelected]?.action()}>
      <i className={infosButton[infosButtonSelected]?.icon} style={{ marginRight: '0.5rem' }}></i> {infosButton[infosButtonSelected]?.label}
    </Button>
  )
}

MainContent.PaymentForecastLabel = PaymentForecastLabel
MainContent.SendTaxDocumentsButton = SendTaxDocumentsButton

export default MainContent
